import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from '../setupAxios';
import { apiUrl } from '../config/api';
import { CATEGORY_LABELS, VALID_CATEGORIES } from '../config/gallery';
import { ROLE_SUPER_ADMIN } from '../context/AuthContext';

function canClearDeviceLog() {
  return Number(localStorage.getItem('role') || 0) === ROLE_SUPER_ADMIN;
}

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatFileSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function GalleryDeviceLog() {
  const { category, deviceId } = useParams();
  const categoryKey = (category || '').toLowerCase();
  const categoryLabel = CATEGORY_LABELS[categoryKey] || category;
  const isValidCategory = VALID_CATEGORIES.includes(categoryKey);

  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchLog = useCallback(async (cancelledRef) => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const response = await axios.get(
        apiUrl(
          `/api/categories/${encodeURIComponent(categoryKey)}/devices/${encodeURIComponent(deviceId)}/log`
        )
      );
      if (!cancelledRef?.cancelled) {
        setLogData(response.data || null);
      }
    } catch (err) {
      if (!cancelledRef?.cancelled) {
        if (err.response?.status === 401) {
          setError('登录已过期，请重新登录');
        } else if (err.response?.status === 404) {
          setError('该设备暂无 Device.log 日志文件');
        } else {
          setError('加载日志失败，请确认后端服务已启动');
        }
        setLogData(null);
        console.error('Device log error:', err);
      }
    } finally {
      if (!cancelledRef?.cancelled) {
        setLoading(false);
      }
    }
  }, [categoryKey, deviceId]);

  useEffect(() => {
    if (!isValidCategory || !deviceId) {
      return undefined;
    }
    const state = { cancelled: false };
    fetchLog(state);
    return () => {
      state.cancelled = true;
    };
  }, [categoryKey, deviceId, isValidCategory, fetchLog, refreshKey]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryKey, deviceId, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleClearLog = async () => {
    if (!canClearDeviceLog()) {
      window.alert('无权限清除，需要高级管理员账号（users.role = 1）');
      return;
    }

    const confirmed = window.confirm(
      `确定要清除设备「${deviceId}」的 Device.log 吗？\n\n此操作不可恢复。`
    );
    if (!confirmed) {
      return;
    }

    setClearing(true);
    setError('');
    setSuccessMsg('');
    try {
      const response = await axios.delete(
        apiUrl(
          `/api/categories/${encodeURIComponent(categoryKey)}/devices/${encodeURIComponent(deviceId)}/log`
        )
      );
      setLogData(null);
      setSuccessMsg(response.data?.message || 'Device.log 已清除');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('无权限清除，需要高级管理员账号（role=1）');
      } else if (err.response?.status === 401) {
        setError('登录已过期，请重新登录');
      } else if (err.response?.status === 404) {
        setError('Device.log 不存在或已被清除');
        setLogData(null);
      } else {
        setError('清除日志失败，请稍后重试');
      }
      console.error('Clear device log error:', err);
    } finally {
      setClearing(false);
    }
  };

  if (!isValidCategory) {
    return <Navigate to="/gallery/flat" replace />;
  }

  if (!deviceId) {
    return <Navigate to={`/gallery/${categoryKey}`} replace />;
  }

  const listUrl = `/gallery/${categoryKey}`;

  return (
    <div className={`page-shell gallery-page--devices gallery-page--log gallery-page--${categoryKey}`}>
      <div className="device-log-header">
        <div className="device-log-header-main">
          <h3 className="device-log-title">{categoryLabel} 设备日志</h3>
          <p className="device-log-subtitle">设备目录：<code className="device-list-code">{deviceId}</code></p>
        </div>
        <div className="device-log-header-actions">
          <button type="button" className="btn btn-secondary btn-compact" onClick={handleRefresh} disabled={loading || clearing} title="刷新">
            {loading ? '刷新中...' : '刷新'}
          </button>
          {logData && (
            <button
              type="button"
              title="清除日志"
              className="btn btn-danger btn-compact"
              onClick={handleClearLog}
              disabled={clearing || loading}
            >
              {clearing ? '清除中...' : '清除日志'}
            </button>
          )}
          <Link to={listUrl} className="btn btn-secondary btn-compact device-log-back-link" title="设备列表">
            返回设备列表
          </Link>
        </div>
      </div>

      {error && <div className="gallery-error">{error}</div>}
      {successMsg && <div className="gallery-success">{successMsg}</div>}

      {loading && !logData ? (
        <div className="loading">加载日志中...</div>
      ) : logData ? (
        <div className="device-log-card">
          <div className="device-log-meta">
            <span>文件：Device.log</span>
            <span>大小：{formatFileSize(logData.size)}</span>
            <span>行数：{logData.lineCount ?? '-'}</span>
            <span>更新时间：{formatDateTime(logData.lastModified)}</span>
            {logData.truncated && (
              <span className="device-log-truncated">仅显示文件尾部（文件过大）</span>
            )}
          </div>
          <pre className="device-log-content">{logData.content || '(空文件)'}</pre>
        </div>
      ) : !loading && successMsg ? (
        <p className="gallery-empty">{successMsg}</p>
      ) : null}
    </div>
  );
}

export default GalleryDeviceLog;
